import { useI18n, translate as t } from "../i18n";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";
import { ChevronDown, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useContactChat } from "../context/ContactChatContext";
import { useScrolledPast } from "../hooks/useScrolledPast";
import ContactForm from "./ContactForm";

/** Same threshold as Back to Top — stack height includes BTT only when it can be visible. */
const SCROLL_FOR_BACK_TO_TOP = 400;

export default function ContactChatPopup() {
  const { t, language } = useI18n("ContactChatPopup");
  const location = useLocation();
  const navigate = useNavigate();
  const titleId = useId();
  const { contactOpen: open, openContactPopup, closeContactPopup, toggleContactPopup } =
    useContactChat();
  const [mounted, setMounted] = useState(false);
  const lockedScrollY = useRef(0);
  const backToTopLikelyVisible = useScrolledPast(SCROLL_FOR_BACK_TO_TOP);

  const { fabBottom, panelBottom } = useMemo(() => {
    if (backToTopLikelyVisible) {
      return {
        fabBottom: "var(--contact-fab-stacked)",
        panelBottom: "calc(1rem + 3.5rem + 0.4rem)",
      };
    }
    return {
      fabBottom: "var(--contact-fab-base)",
      panelBottom: "calc(1rem + 3.5rem + 0.4rem)",
    };
  }, [backToTopLikelyVisible]);

  useEffect(() => setMounted(true), []);

  const isHome = location.pathname === "/";

  /** Legacy/bookmark links: open once then strip #contact so refresh does not reopen */
  const syncHashToPopup = useCallback(() => {
    if (location.hash !== "#contact") return;
    openContactPopup();
    navigate({ pathname: location.pathname, search: location.search, hash: "" }, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate, openContactPopup]);

  useEffect(() => {
    syncHashToPopup();
  }, [syncHashToPopup]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeContactPopup();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeContactPopup]);

  useEffect(() => {
    if (!open) return;

    const body = document.body;
    const root = document.documentElement;
    const previousBodyStyles = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };
    const previousRootOverflow = root.style.overflow;

    lockedScrollY.current = window.scrollY;
    root.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${lockedScrollY.current}px`;
    body.style.width = "100%";

    return () => {
      root.style.overflow = previousRootOverflow;
      body.style.overflow = previousBodyStyles.overflow;
      body.style.position = previousBodyStyles.position;
      body.style.top = previousBodyStyles.top;
      body.style.width = previousBodyStyles.width;
      window.scrollTo(0, lockedScrollY.current);
    };
  }, [open]);

  const fabContactId = isHome ? "contact" : undefined;

  return (
    <>
      <motion.button
        type="button"
        id={fabContactId}
        initial={false}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
        transition={{ type: "spring", stiffness: 420, damping: 28 }}
        style={{ bottom: fabBottom }}
        className={`contact-chat-fab fixed right-4 z-[130] flex h-14 w-14 items-center justify-center rounded-2xl border text-lg font-bold tracking-tight shadow-xl backdrop-blur-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
          open
            ? "border-gold/70 bg-[#031008] text-gold shadow-[0_0_28px_rgba(241,200,106,0.28)] ring-2 ring-brand/20"
            : "border-gold/35 bg-bg-dark text-gold shadow-black/30 hover:border-brand/70 hover:text-brand"
        }`}
        aria-expanded={open}
        aria-controls={open ? "contact-chat-panel" : undefined}
        aria-label={t(open ? "Close contact form" : "Open contact form")}
        onClick={toggleContactPopup}
      >
        {open ? (
          <ChevronDown className="h-6 w-6 text-gold" strokeWidth={2.2} aria-hidden />
        ) : (
          <span className="font-display">{t("EZ")}</span>
        )}
      </motion.button>

      {mounted
        ? createPortal(
            <AnimatePresence>
              {open ? (
                <>
                  <motion.button
                    type="button"
                    key="contact-chat-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-[105] bg-black/62 backdrop-blur-[3px]"
                    aria-label={t("Close contact")}
                    onClick={closeContactPopup}
                  />
                  <motion.div
                    key="contact-chat-panel"
                    id="contact-chat-panel"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={titleId}
                    initial={{ opacity: 0, y: 16, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 12, scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    style={{
                      bottom: panelBottom,
                      clipPath:
                        "polygon(18px 0, calc(100% - 18px) 0, 100% 18px, 100% calc(100% - 18px), calc(100% - 18px) 100%, 18px 100%, 0 calc(100% - 18px), 0 18px)",
                    }}
                    className="contact-chat-panel fixed left-2 right-2 z-[110] flex max-h-[calc(100dvh-5.25rem)] flex-col overflow-hidden border border-gold/35 bg-[#020704] shadow-[0_28px_90px_rgba(0,0,0,0.58),0_0_42px_rgba(66,255,117,0.08)] sm:left-auto sm:right-4 sm:w-[min(calc(100vw-2rem),560px)] md:right-5 md:max-h-[min(78svh,35rem)] md:w-[min(calc(100vw-2.5rem),560px)] lg:w-[580px] xl:max-h-[min(78svh,36rem)] xl:w-[600px] 2xl:max-h-[min(86svh,48rem)] 2xl:w-[680px] [@media_(min-width:1920px)_and_(min-height:1000px)]:max-h-[min(88svh,52rem)]"
                  >
                    <div className="relative flex shrink-0 items-start justify-between gap-3 border-b border-gold/20 px-3.5 pb-2 pt-3 md:px-4 md:pb-2.5 md:pt-3 2xl:px-6 2xl:pb-4 2xl:pt-6">
                      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(241,200,106,0.07)_1px,transparent_1px),linear-gradient(90deg,rgba(66,255,117,0.055)_1px,transparent_1px)] bg-[size:52px_52px] opacity-45" aria-hidden />
                      <div className="min-w-0 pr-2">
                        <p className="relative font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold md:text-xs md:tracking-[0.22em] 2xl:text-xl 2xl:tracking-[0.26em]">
                          {t("[ CONTACT ]")}</p>
                        <h2 id={titleId} className="relative mt-0.5 max-w-full whitespace-nowrap font-display text-[clamp(11px,3.7vw,15px)] font-bold uppercase leading-tight tracking-[-0.025em] text-white md:mt-1.5 md:text-[1.2rem] md:leading-[1.05] md:tracking-[-0.015em] xl:text-[1.35rem] 2xl:mt-3 2xl:text-[1.45rem]">
                          {t("Let's create something great")}</h2>
                        <p className="relative mt-0.5 max-w-[44ch] text-[clamp(11px,3.2vw,13px)] leading-tight text-white/70 [text-wrap:balance] [@media_(max-height:700px)]:hidden md:mt-1.5 md:text-sm md:leading-snug md:[@media_(max-height:700px)]:block 2xl:mt-3 2xl:text-lg 2xl:leading-normal">
                          {t("Tell us about your project — we read every message.")}</p>
                      </div>
                      <button
                        type="button"
                        className="relative z-[1] shrink-0 border border-gold/35 bg-black/50 p-2 text-gold transition-colors hover:border-brand/70 hover:bg-brand/10 hover:text-brand 2xl:p-2.5"
                        aria-label={t("Close")}
                        onClick={closeContactPopup}
                      >
                        <X className="h-4.5 w-4.5 md:h-5 md:w-5" strokeWidth={2} />
                      </button>
                    </div>

                    <div
                      data-lenis-prevent
                      data-lenis-prevent-wheel
                      data-lenis-prevent-touch
                      className="relative min-h-0 flex-auto touch-pan-y overflow-y-auto overscroll-contain px-3.5 pb-3 pt-2 [scrollbar-gutter:stable] md:px-4 md:pb-3 md:pt-2 2xl:px-6 2xl:pb-5 2xl:pt-4"
                      style={{ WebkitOverflowScrolling: "touch" }}
                    >
                      <div className="pointer-events-none absolute inset-x-3.5 top-0 h-px bg-gradient-to-r from-transparent via-brand/45 to-transparent md:inset-x-6" aria-hidden />
                      <ContactForm compact embeddedPopup motionDelay={0} />
                    </div>
                  </motion.div>
                </>
              ) : null}
            </AnimatePresence>,
            document.body
          )
        : null}
    </>
  );
}
